import React, { useState, useEffect, useMemo } from 'react';
import { fetchStatesApi, fetchDistrictsByStateApi, fetchBlocksByDistrictApi, fetchVillagesByBlockAreaApi, getCurrentUserRoleType, getCurrentUserEntity } from '../../../../../core/data/services/adminService';
import CommonSelect, { Option } from '../../../../../core/common/commonSelect';

interface SchoolFilters {
  name: string;
  address: string;
  phone: string;
  schoolCode: string;
  stateId: number | null;
  districtId: number | null;
  blockAreaId: number | null;
  villageWardId: number | null;
}

interface SchoolFilterProps {
  filters: SchoolFilters;
  onFilterChange: (filters: SchoolFilters) => void;
  onApply: () => void;
  onReset: () => void;
  isLoading?: boolean;
}

const SchoolFilter: React.FC<SchoolFilterProps> = ({
  filters,
  onFilterChange,
  onApply,
  onReset,
  isLoading = false
}) => {
  const [states, setStates] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);
  const [blocks, setBlocks] = useState<any[]>([]);
  const [villages, setVillages] = useState<any[]>([]);
  const [selectedState, setSelectedState] = useState<number | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<number | null>(null);
  const [selectedBlock, setSelectedBlock] = useState<number | null>(null);
  const [selectedVillage, setSelectedVillage] = useState<number | null>(null);

  // Fetch states on component mount (skip or preselect for scoped admins)
  useEffect(() => {
    const roleType = getCurrentUserRoleType();
    const entity = getCurrentUserEntity();

    // Helper to set filters in one go
    const applyFilters = (next: Partial<SchoolFilters>) => {
      onFilterChange({
        ...filters,
        ...next,
      } as SchoolFilters);
    };

    if (roleType === 'villageWard' && entity?.villageWards?.id) {
      const state = entity.states ? { id: entity.states.id, name: entity.states.name } : null;
      const district = entity.districts ? { id: entity.districts.id, name: entity.districts.name } : null;
      const block = entity.blockAreas ? { id: entity.blockAreas.id, name: entity.blockAreas.name } : null;
      const village = { id: entity.villageWards.id, name: entity.villageWards.name };
      if (state) setStates([state]);
      if (district) setDistricts([district]);
      if (block) setBlocks([block]);
      setVillages([village]);
      if (state) setSelectedState(state.id);
      if (district) setSelectedDistrict(district.id);
      if (block) setSelectedBlock(block.id);
      setSelectedVillage(village.id);
      applyFilters({
        stateId: state ? state.id : null,
        districtId: district ? district.id : null,
        blockAreaId: block ? block.id : null,
        villageWardId: village.id,
      });
      return;
    }

    if (roleType === 'block' && (entity?.blockAreas?.id || entity?.villageWards?.blockAreaId)) {
      const state = entity.states ? { id: entity.states.id, name: entity.states.name } : null;
      const district = entity.districts ? { id: entity.districts.id, name: entity.districts.name } : null;
      const blockId = entity.blockAreas?.id || entity.villageWards?.blockAreaId;
      const block = blockId ? { id: blockId, name: entity.blockAreas?.name || '' } : null;
      if (state) setStates([state]);
      if (district) setDistricts([district]);
      if (block) setBlocks([block]);
      if (state) setSelectedState(state.id);
      if (district) setSelectedDistrict(district.id);
      if (block) setSelectedBlock(block.id);
      applyFilters({
        stateId: state ? state.id : null,
        districtId: district ? district.id : null,
        blockAreaId: block ? block.id : null,
        villageWardId: null,
      });
      // Populate villages for this block
      if (block) {
        fetchVillagesByBlockAreaApi(block.id).then((data) => setVillages(data));
      }
      return;
    }

    if (roleType === 'district' && (entity?.districts?.id || entity?.villageWards?.districtId)) {
      const state = entity.states ? { id: entity.states.id, name: entity.states.name } : null;
      const districtId = entity.districts?.id || entity.villageWards?.districtId;
      const district = { id: districtId, name: entity.districts?.name || '' };
      if (state) setStates([state]);
      setDistricts([district]);
      if (state) setSelectedState(state.id);
      setSelectedDistrict(district.id);
      applyFilters({
        stateId: state ? state.id : null,
        districtId: district.id,
        blockAreaId: null,
        villageWardId: null,
      });
      // Populate blocks for this district
      fetchBlocksByDistrictApi(district.id).then((data) => setBlocks(data));
      return;
    }

    if (roleType === 'state' && entity?.states?.id) {
      const state = { id: entity.states.id, name: entity.states.name };
      setStates([state]);
      setSelectedState(entity.states.id);
      const newFilters = { ...filters, stateId: entity.states.id } as SchoolFilters;
      onFilterChange(newFilters);
      // Populate districts for this state
      fetchDistrictsByStateApi(state.id).then((data) => setDistricts(data));
      return;
    }

    // Fallback: Unscoped - fetch full states
    fetchStatesApi().then((data) => {
      setStates(data);
    });
  }, []);

  // Fetch districts when state changes
  useEffect(() => {
    if (selectedState) {
      fetchDistrictsByStateApi(selectedState).then((data) => {
        setDistricts(data);
      });
    } else {
      setDistricts([]);
    }
  }, [selectedState]);

  // Fetch blocks when district changes
  useEffect(() => {
    if (selectedDistrict) {
      fetchBlocksByDistrictApi(selectedDistrict).then((data) => {
        setBlocks(data);
      });
    } else {
      setBlocks([]);
    }
  }, [selectedDistrict]);

  // Fetch villages when block changes
  useEffect(() => {
    if (selectedBlock) {
      fetchVillagesByBlockAreaApi(selectedBlock).then((data) => {
        setVillages(data);
      });
    } else {
      setVillages([]);
    }
  }, [selectedBlock]);

  const handleLocationChange = (type: 'state' | 'district' | 'block' | 'village', value: number | null) => {
    const newFilters = { ...filters };

    switch (type) {
      case 'state':
        setSelectedState(value);
        setSelectedDistrict(null);
        setSelectedBlock(null);
        setSelectedVillage(null);
        newFilters.stateId = value ?? null;
        newFilters.districtId = null;
        newFilters.blockAreaId = null;
        newFilters.villageWardId = null;
        break;
      case 'district':
        setSelectedDistrict(value);
        setSelectedBlock(null);
        setSelectedVillage(null);
        newFilters.districtId = value ?? null;
        newFilters.blockAreaId = null;
        newFilters.villageWardId = null;
        break;
      case 'block':
        setSelectedBlock(value);
        setSelectedVillage(null);
        newFilters.blockAreaId = value ?? null;
        newFilters.villageWardId = null;
        break;
      case 'village':
        setSelectedVillage(value);
        newFilters.villageWardId = value ?? null;
        break;
    }

    onFilterChange(newFilters);
  };

  const handleFilterChange = (key: keyof SchoolFilters, value: string) => {
    onFilterChange({
      ...filters,
      [key]: value
    });
  };

  const handleReset = () => {
    const roleType = getCurrentUserRoleType();
    const entity = getCurrentUserEntity();

    // Reset to fixed scope if scoped admin
    if (roleType && roleType !== 'admin' && roleType !== 'schoolHm') {
      if (entity?.states?.id) {
        setStates(entity.states ? [{ id: entity.states.id, name: entity.states.name }] : []);
        setSelectedState(entity.states.id);
      }
      if (roleType !== 'state' && (entity?.districts?.id || entity?.villageWards?.districtId)) {
        const districtId = entity.districts?.id || entity.villageWards?.districtId;
        const districtName = entity.districts?.name || '';
        setDistricts([{ id: districtId, name: districtName }]);
        setSelectedDistrict(districtId);
      } else {
        setDistricts([]);
        setSelectedDistrict(null);
      }
      if ((roleType === 'block' || roleType === 'villageWard') && (entity?.blockAreas?.id || entity?.villageWards?.blockAreaId)) {
        const blockId = entity.blockAreas?.id || entity.villageWards?.blockAreaId;
        const blockName = entity.blockAreas?.name || '';
        setBlocks([{ id: blockId, name: blockName }]);
        setSelectedBlock(blockId);
      } else {
        setBlocks([]);
        setSelectedBlock(null);
      }
      if (roleType === 'villageWard' && entity?.villageWards?.id) {
        setVillages([{ id: entity.villageWards.id, name: entity.villageWards.name }]);
        setSelectedVillage(entity.villageWards.id);
      } else {
        setVillages([]);
        setSelectedVillage(null);
      }

      onFilterChange({
        ...filters,
        stateId: entity?.states?.id ?? null,
        districtId: entity?.districts?.id ?? (entity?.villageWards?.districtId ?? null),
        blockAreaId: entity?.blockAreas?.id ?? (entity?.villageWards?.blockAreaId ?? null),
        villageWardId: entity?.villageWards?.id ?? null,
      } as SchoolFilters);
      return;
    }

    // Unscoped reset
    setSelectedState(null);
    setSelectedDistrict(null);
    setSelectedBlock(null);
    setSelectedVillage(null);
    onReset();
  };

  const roleTypeForDisable = getCurrentUserRoleType();
  // State Option
  const stateOptions: Option[] = states.map((s: any) => ({
    value: String(s.id),
    label: s.name,
  }));

  const currentState: Option | null =
    selectedState != null
      ? stateOptions.find(o => o.value === String(selectedState)) ?? null
      : null;

  // District option
  const districtOptions: Option[] = useMemo(
    () =>
      districts.map((d: any) => ({
        value: String(d.id),
        label: d.name,
      })),
    [districts]
  );

  const currentDistrict: Option | null = useMemo(
    () =>
      selectedDistrict != null
        ? districtOptions.find((o) => o.value === String(selectedDistrict)) ?? null
        : null,
    [selectedDistrict, districtOptions]
  );

  // Block option
  const blockOptions: Option[] = useMemo(
    () =>
      blocks.map((b: any) => ({
        value: String(b.id),
        label: b.name,
      })),
    [blocks]
  );

  const currentBlock: Option | null = useMemo(
    () =>
      selectedBlock != null
        ? blockOptions.find((o) => o.value === String(selectedBlock)) ?? null
        : null,
    [selectedBlock, blockOptions]
  );

  // Village option 
  const villageOptions: Option[] = useMemo(
    () =>
      villages.map((v: any) => ({
        value: String(v.id),
        label: v.name,
      })),
    [villages]
  );

  const currentVillage: Option | null = useMemo(
    () =>
      selectedVillage != null
        ? villageOptions.find((o) => o.value === String(selectedVillage)) ?? null
        : null,
    [selectedVillage, villageOptions]
  );

  return (
    <div className="dropdown-menu drop-width">
      <form>
        <div className="d-flex align-items-center border-bottom p-3">
          <h4>Filter</h4>
        </div>
        <div className="p-3 border-bottom pb-0">
          <div className="row">
            {/* State Filter */}
            { }

            {roleTypeForDisable === 'admin' && (
              <div className="col-md-6 mb-3">
                <label className="form-label">State</label>
                <CommonSelect
                  options={stateOptions}
                  defaultValue={currentState}
                  isDisabled={isLoading}
                  onChange={(opt) => {
                    const id = opt?.value ? Number(opt.value) : null;
                    handleLocationChange("state", id);
                  }}
                />
              </div>
            )}

            {/* District Filter */}
            {(roleTypeForDisable === 'admin' || roleTypeForDisable === 'state') && (
              <div className="col-md-6 mb-3">
                <label className="form-label">District</label>
                <CommonSelect
                  options={districtOptions}
                  defaultValue={currentDistrict}
                  isDisabled={!selectedState || isLoading}
                  onChange={(opt) => {
                    const id = opt?.value ? Number(opt.value) : null;
                    handleLocationChange("district", id);
                  }}
                />
              </div>
            )}

            {/* Block Area Filter */}
            {(roleTypeForDisable === 'admin' || roleTypeForDisable === 'state' || roleTypeForDisable === 'district') && (
              <div className="col-md-6 mb-3">
                <label className="form-label">Block Area</label>
                <CommonSelect
                  options={blockOptions}
                  defaultValue={currentBlock}
                  isDisabled={!selectedDistrict || isLoading}
                  onChange={(opt) => {
                    const id = opt?.value ? Number(opt.value) : null;
                    handleLocationChange("block", id);
                  }}
                />
              </div>
            )}

            {/* Village/Ward Filter */}
            {(roleTypeForDisable === 'admin' || roleTypeForDisable === 'state' || roleTypeForDisable === 'district' || roleTypeForDisable === 'block') && (
              <div className="col-md-6 mb-3">
                <label className="form-label">Village/Ward</label>
                <CommonSelect
                  options={villageOptions}
                  defaultValue={currentVillage}                 // must be an Option from options
                  isDisabled={!selectedBlock || isLoading}
                  onChange={(opt) => {
                    const id = opt?.value ? Number(opt.value) : null;
                    handleLocationChange("village", id);
                  }}
                />
              </div>
            )}

            {/* Name Filter */}
            <div className="col-md-6 mb-3">
              <label className="form-label">Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter school name..."
                value={filters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>
        <div className="p-3 d-flex align-items-center justify-content-end">
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={handleReset}
            disabled={isLoading}
          >
            Reset
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onApply}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </span>
                Applying...
              </>
            ) : (
              'Apply'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SchoolFilter;
